import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useTheme } from 'emotion-theming'
import {
	isBrowser,
	generateAffiliateLink,
	generateLinkWithAddedParams,
	getParagraphSections,
	makeImgResizerUrl,
	pushGtmData,
	splitParagraphString,
} from '@jelly/utils'
import PropTypes, { array, bool, object, shape, string } from 'prop-types'

// import components
import {
	AnchorTag,
	BannerRibbon,
	CheckMark,
	PhoneIcon,
	ContentContainer,
	MonetizationButton,
	Warning,
} from '@jelly/components'
import { Link } from '@jelly/ui'

const ARROW_POINT_DOWN = 180
const ARROW_POINT_RIGHT = 90
const ARROW_POINT_UP = 0

// styled components via emotion
const BannerWrapper = styled.div`
	background: ${({ isFeatured, theme }) =>
		isFeatured ? theme.bestPicks.featuredTitleBackground : theme.simpleHeader.breadcrumbsBackground};
	color: ${({ isFeatured, theme }) =>
		isFeatured ? theme.bestPicks.featuredTitleColor : theme.bestPicks.serviceTitleColor};
	position: relative;
`
const BannerRibbonWrapper = styled.div`
	position: absolute;
	top: -4px;
	left: 4px;
`

const ServiceTitle = styled.h2`
	${({ theme }) => theme.fonts.header};
	align-items: center;
	display: flex;
	font-size: 18px;
	height: auto;
	margin: auto 0;
	margin-left: ${({ isFeatured }) => (isFeatured ? '2.75rem' : '0')};
	padding: 10px;

	${({ theme }) => theme.MQ.M} {
		font-size: 18px;
		height: 55px;
		padding: 0 0 0 16px;
	}
`
const ReviewCardWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: auto;
	margin: 20px 0 50px 0;
	width: 100%;

	p {
		width: 100%;
	}

	${({ theme }) => theme.MQ.M} {
		margin: 20px 0;
	}
`
const TopHalfContainer = styled.div`
	display: block;
	flex-direction: column;
	overflow: hidden;

	${({ theme }) => theme.MQ.M} {
		${({ showReview }) => (showReview ? `display: block` : `display: flex; flex-direction: row;`)};
		margin: 25px 0;
	}
`
const ServiceInfoWrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin: 20px 0;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		display: block;
		border-right: 1px solid ${({ theme }) => theme.colors.grey01};
		float: left;
		margin: 20px 20px 40px 20px;
		width: 50%;
	}
`
const ServiceInfo = styled.div`
	align-items: flex-start;
	display: flex;
	flex-direction: column;
	margin: 0 10px;
	text-align: left;

	${({ theme }) => theme.MQ.M} {
		margin: 0 0 20px 0;
	}
`
const ServiceLogoWrapper = styled.div`
	margin-bottom: 30px;
`
const ServiceLogo = styled.img`
	height: auto;
	padding-right: 15px;
	width: auto;
`
const ServiceName = styled.div`
	${({ theme }) => theme.fonts.header};
	color: ${({ theme }) => theme.colors.tertiary};
	font-size: 20px;

	${({ as, theme }) =>
		as &&
		`&:hover {
		color: ${theme.navigation.linkHoverColor};
	}`}
`
const StatisticsWrapper = styled.div`
	margin: 0 0 30px 10px;
	width: 95%;

	${({ theme }) => theme.MQ.M} {
		margin: 0 0 40px 0;
	}
`
const Statistics = styled.div`
	align-items: center;
	display: flex;
	flex-direction: row;
	margin-bottom: 12px;
`
const Stat = styled.div`
	display: block;
	color: ${({ theme }) => theme.colors.primary};
	font-weight: 600;
	max-width: 600px;
	margin-left: 13px;
	width: 100%;
`
const ButtonWrapper = styled.div`
	display: flex;
	justify-content: flex-start;
	padding-right: 10px;
	width: 100%;
`
const ReviewContext = styled.div`
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		width: ${({ showReview }) => (showReview ? `100%` : `calc(50% - 40px)`)};
	}
`
const ReviewContentContainer = styled.div`
	display: ${({ showReview }) => (showReview || !isBrowser() ? `block` : `none`)};
	${({ theme }) => theme.MQ.M} {
		display: ${({ showReview }) => (showReview ? `block` : `none`)};
	}
`
const ArrowCharacter = styled.div`
	${({ theme }) => theme.triangle};
	display: inline-block;
	margin-left: 3px;
	transform: ${({ arrowDirection = ARROW_POINT_DOWN }) => `rotate(${arrowDirection}deg)`};
`
const ReadMoreLink = styled(Link)`
	${({ theme }) => theme.links.default};
	${({ theme }) => theme.fonts.header};
	cursor: pointer;
	display: inline-block;
	text-decoration: underline;
	text-transform: uppercase;

	&:hover {
		${ArrowCharacter} {
			border-bottom-color: ${({ theme }) => theme.colors.tertiary};
		}
	}
`
const ClickToCallWrapper = styled(Link)`
	align-items: center;
	display: flex;
	height: 2.25rem;
	margin: 1rem 0 0;

	svg {
		fill: ${({ theme }) => theme.colors.tertiary};
	}
	&:hover {
		span {
			color: ${({ theme }) => theme.navigation.linkHoverColor};
		}
		svg {
			fill: ${({ theme }) => theme.navigation.linkHoverColor};
		}
	}
`
const ClickToCallTelNumber = styled.span`
	color: ${({ theme }) => theme.colors.tertiary};
	font-weight: bold;
`

ReviewCard.propTypes = {
	canonical: string,
	categoryWidgetId: string,
	phoneNumber: string,
	pricing: object,
	fullTitle: string.isRequired,
	isFeaturedBestPick: bool,
	isJumpLink: bool.isRequired,
	review: shape({
		cons: array,
		hasReviewEnabled: bool,
		reviewContent: string,
		pros: array,
	}),
	rank: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	reviewPath: string.isRequired,
	serviceName: string.isRequired,
	thumbnail: string.isRequired,
	url: string.isRequired,
}
/**
 * Review Card that displays the best pick preview review and displays full review
 *
 * @param {String} props.canonical :: link to service review
 * @param {String} props.categoryWidgetId :: widget id set on the category level
 * @param {String} props.fullTitle :: best picks of service title
 * @param {Boolean} props.isFeaturedBestPick :: flag to determine if service is marked as featured best pick service
 * @param {Boolean} props.isJumpLink :: flag to determine which review to reference
 * @param {String} props.phoneNumber :: string containing click to call phone number
 * @param {Object} props.pricing :: monetization data for service
 * @param {Object} props.review :: data containing pros, cons, review
 * @param {String} props.serviceName :: name of service/product
 * @param {String} props.thumbnail :: image path of service logo
 */
export function ReviewCard({
	canonical,
	categoryWidgetId,
	fullTitle,
	isJumpLink,
	isFeaturedBestPick,
	phoneNumber,
	pricing,
	review: { cons, hasReviewEnabled, reviewContent, pros },
	reviewPath,
	serviceName,
	thumbnail,
	rank,
}) {
	const {
		bestPicks: { checkMark },
		MATCH_MEDIA,
	} = useTheme()
	const anchorTag = serviceName.replace(/\s/g, '-')
	const [showReview, setShowReview] = useState(false)
	const imgSrc = makeImgResizerUrl({ src: thumbnail, method: 'w', w: 150 })
	const splitParagraphs = splitParagraphString(reviewContent)
	const previewParagraphs = getParagraphSections(splitParagraphs, 0, 2)
	const restOfParagraphs = getParagraphSections(splitParagraphs, 2)
	const cardType = 'reviewCard'
	const [affiliateLink, setAffiliateLink] = useState(generateAffiliateLink(pricing))
	useEffect(() => {
		if (isBrowser()) {
			const isDesktop = window.matchMedia(MATCH_MEDIA.M).matches
			!isDesktop && setShowReview(true)
		}
		setAffiliateLink(
			generateLinkWithAddedParams({
				link: affiliateLink,
				rank: rank,
				type: cardType,
				tabTitle: 'undefined',
			})
		)
	}, []) //eslint-disable-line

	const handleRfqButtonClick = () => {
		pushGtmData({
			event: 'gaEvent',
			gaEventCategory: 'Compare Quotes Click',
			gaEventAction: serviceName,
			gaEventLabel: 'Review Summary Card',
			gaEventNoninteraction: false,
		})
	}
	const handleQtClick = (location = 'Button') => {
		pushGtmData({
			event: 'gaEvent',
			gaEventCategory: 'Vist Site Click',
			gaEventAction: serviceName,
			gaEventLabel: `Review Summary Card ${location}`,
			gaEventNoninteraction: false,
		})
	}

	return (
		<>
			<BannerWrapper id={anchorTag} isFeatured={isFeaturedBestPick}>
				<>
					{isFeaturedBestPick && (
						<BannerRibbonWrapper>
							<BannerRibbon />
						</BannerRibbonWrapper>
					)}
					<ServiceTitle isFeatured={isFeaturedBestPick}>{`${serviceName}: ${fullTitle}`}</ServiceTitle>
				</>
			</BannerWrapper>
			<ReviewCardWrapper>
				<TopHalfContainer showReview={showReview}>
					<ServiceInfoWrapper>
						<ServiceInfo>
							<ServiceLogoWrapper
								id={`${serviceName}-link-company-logo`}
								{...(affiliateLink && {
									alt: serviceName,
									as: AnchorTag,
									href: affiliateLink,
									onClick: () => handleQtClick('Logo'),
								})}
							>
								<ServiceLogo className="lazyload" alt={serviceName} data-src={imgSrc} />
							</ServiceLogoWrapper>
							<ServiceName
								id={`${serviceName}-link-company-name`}
								{...(affiliateLink && {
									as: AnchorTag,
									href: affiliateLink,
									onClick: () => handleQtClick('Service Name'),
								})}
							>
								{serviceName}
							</ServiceName>
						</ServiceInfo>
						<StatisticsWrapper>
							{pros &&
								pros.map((pro, i) => {
									return (
										<Statistics key={i}>
											<CheckMark alt="pro" fill={checkMark} />
											<Stat>{pro}</Stat>
										</Statistics>
									)
								})}
							{cons &&
								cons.map((con, i) => {
									return (
										<Statistics key={i}>
											<Warning alt="con" fill={checkMark} />
											<Stat>{con}</Stat>
										</Statistics>
									)
								})}
						</StatisticsWrapper>
						<ButtonWrapper>
							<MonetizationButton
								categoryWidgetId={categoryWidgetId}
								onQtButtonClick={handleQtClick}
								onRfqButtonClick={handleRfqButtonClick}
								pricing={pricing}
								rank={rank}
								serviceName={anchorTag}
								sectionType="reviews"
								showArrow={false}
								tabTitle="undefined"
								templateType="mbp"
								type={cardType}
							/>
						</ButtonWrapper>
						{phoneNumber && (
							<ClickToCallWrapper href={`tel:${phoneNumber}`}>
								<PhoneIcon height="20px" width="20px" />
								<ClickToCallTelNumber>{phoneNumber}</ClickToCallTelNumber>
							</ClickToCallWrapper>
						)}
					</ServiceInfoWrapper>
					<ReviewContext showReview={showReview}>
						{isJumpLink ? (
							<>
								<ContentContainer content={previewParagraphs} />
								{restOfParagraphs && (
									<>
										<ReviewContentContainer showReview={showReview}>
											<ContentContainer content={restOfParagraphs} />
										</ReviewContentContainer>
										<ReadMoreLink
											id={`mbp_${anchorTag}_reviews_readmore`}
											onClick={() => setShowReview(!showReview)}
										>
											{showReview ? `Less` : `More`}
											<ArrowCharacter
												arrowDirection={showReview ? ARROW_POINT_UP : ARROW_POINT_DOWN}
											/>
										</ReadMoreLink>
									</>
								)}
							</>
						) : (
							<>
								<ContentContainer content={reviewContent} />
								{hasReviewEnabled && (
									<ReadMoreLink href={reviewPath} id={`mbp_${anchorTag}_reviews_readreview`}>
										{`Read ${serviceName} Review`}
										<ArrowCharacter arrowDirection={ARROW_POINT_RIGHT} />
									</ReadMoreLink>
								)}
							</>
						)}
					</ReviewContext>
				</TopHalfContainer>
			</ReviewCardWrapper>
		</>
	)
}
